/**
 * @file afterDelivery.js
 * @version 1.3.2
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-09
 * @modified 2026-11-03
 * @description Editing an order, and its POs, once it is Delivered or
 *              invoiced (Cam, 9 Oct 2026: "we need to be able to edit SOs and
 *              POs even if the order has been delivered … details that only
 *              pertain to ourselves and our own records"). The screens' half
 *              of utils/afterDeliveryEdit.js and utils/poEditScope.js on the
 *              server — which cells stay closed, and the words that say why.
 *
 *              Open: words and notes (sale and purchase descriptions,
 *              references, contract numbers), item codes, the supplier,
 *              delivery terms and haulier. Closed, greyed with the reason —
 *              never hidden: quantities, adding or removing lines; once
 *              invoiced the sale price, tax and additives; on a line bought on
 *              a PO its additives. Supplied quantities and PO costs keep their
 *              own windows (Add / Update Supplied Qty, Cost & Supplied Qty).
 *
 *              1.1.0 (review): a line may be ADDED to a delivered or invoiced
 *              order, as before (it gets no delivered PO); removing one stays
 *              closed. A reason is optional. The Unit Cost cell is closed
 *              too (Cost & Supplied Qty, or the bill).
 *
 *              1.2.0 (2026-10-19, Cam — "allow the edit button to work in
 *              PO-H modal and PO search table"): Edit on a PO-H opens its own
 *              window (HaulagePoDetailsModal — delivery, collection, notes;
 *              poEditOpensHaulageWindow). A delivered order's PO-H records its
 *              supplied quantities through "Cost & Supplied Qty" beside it,
 *              as a PO-D's does.
 *
 *              1.3.0 (2026-10-30, Cam — SO-D10442): a line's UNITS (Units,
 *              Weight, Pallets, Other) are corrected after delivery — the
 *              quantity stays, the money follows the corrected tonnage by the
 *              line's answer to "what was the price per?"
 *              (utils/unitsAfterDelivery). orderClosedCells takes the
 *              server's per-line blocks (`units_correction` on the order) for
 *              the grid's `unitsBlock`; applyUnitsCorrections keeps each
 *              corrected line's price and cost as its answer gives them,
 *              worked from the line as loaded; unitsCorrectionFlags says so on
 *              the line, with the choice. The PO editor greys units on a line
 *              bought for an order line — they are corrected on the order.
 *              1.3.1 (2026-11-01): the invoiced banner says units can be
 *              corrected too (the invoice offered the change, a paid one
 *              flagged).
 */

import { unitsChange, unitsMoneyPlan, unitsMoneyChoices, unitCorrectionNote, unitsMoneyText } from './unitsAfterDelivery';

/** Is the order past ordinary editing, and how? */
export function orderEditState(order) {
  const delivered = order?.delivery_status === 'Delivered';
  const invoiced = order?.invoiced_status === 'Invoiced';
  return { delivered, invoiced, any: delivered || invoiced };
}

/** The Edit action's title on Search Customer Orders and the order window. */
export function orderEditTitle(order, { restricted = false } = {}) {
  const s = orderEditState(order);
  if (s.invoiced) return 'Edit — invoiced: words, item codes, supplier, terms and haulier can be corrected (the invoice is offered the changes); quantities and prices stay as invoiced, lines are not removed';
  if (s.delivered) return 'Edit — delivered: words, item codes, units, supplier, terms and haulier can be corrected; quantities stay as delivered, lines are not removed';
  return restricted ? 'Edit (Restricted)' : 'Edit';
}

/**
 * The order grid's closed cells (OrderLinesGrid `closed`), or null before
 * delivery.
 */
export function orderClosedCells(state, unitsCorrection = null) {
  if (!state?.any) return null;
  const blocks = unitsCorrection?.lines || {};
  const word = state.invoiced ? 'invoiced' : 'delivered';
  // Adding a line stays open, as it always was (it is not joined onto a
  // delivered PO); removing one is closed.
  // A cost on a line bought on a PO changes through Cost & Supplied Qty on
  // its PO, or the bill. A line from stock has neither: its cost stays open
  // until the order is invoiced (Cam, 3 Nov 2026 — SO-D10642).
  const fields = new Set(['qty', 'remove']);
  if (state.invoiced) ['price', 'tax', 'additives', 'cost'].forEach(f => fields.add(f));
  return {
    fields,
    // Additives change what was bought on the PO; so does its cost.
    poLineFields: new Set(state.invoiced ? ['additives'] : ['additives', 'cost']),
    reason: `This order is ${word}, so its quantities stay as they are and lines are not removed`
      + (state.invoiced ? ', and so do its prices, tax, additives and costs (raise a credit or a debit for a price)' : '')
      + '. Supplied quantities change through Add / Update Supplied Qty'
      + (state.invoiced ? '.' : '; the cost of a line bought on a PO through Cost & Supplied Qty on its PO, or the bill — a line from stock takes its cost here.'),
    // Units corrected after delivery (1.3.0): the server's words for a line
    // whose units cannot be (an invoiced order, a split family's shared PO
    // line, a PO made in Xero, added tonnage).
    unitsBlock: (line) => (line && line.id != null ? (blocks[line.id] || blocks[String(line.id)] || null) : null)
  };
}

/**
 * Each line's price and cost after a units correction, worked from the line
 * as LOADED (`originals`: Map id → the stored line) and the line's answer
 * (`units_basis`: 'old' — the default — 'new' or 'typed', with
 * `units_typed_price` / `units_typed_cost`). A corrected line is marked
 * `_unitsMoney`; one whose units go back to what they were gets its price and
 * cost back. Idempotent — run on every change of the lines.
 *
 * @param {object[]} lines
 * @param {Map} originals
 * @param {(line) => object|null} itemOf
 * @param {{ canViewCosts?: boolean }} [opts]
 */
export function applyUnitsCorrections(lines, originals, itemOf = () => null, { canViewCosts = true } = {}) {
  if (!originals || !originals.size) return lines;
  let changed = false;
  const out = (lines || []).map(line => {
    const orig = line && originals.get(line.id);
    if (!orig) return line;
    if (!unitsChange(orig, line)) {
      if (!line._unitsMoney) return line;
      changed = true;
      const { _unitsMoney, units_basis, units_typed_price, units_typed_cost, ...rest } = line;
      return { ...rest, unit_price: orig.unit_price, cost_price: orig.cost_price };
    }
    const item = itemOf(line);
    const plan = unitsMoneyPlan({
      before: orig, after: line, itemUnitWeightKg: item?.unit_weight_kg ?? orig.item_unit_weight_kg,
      price: orig.unit_price, cost: orig.cost_price,
      basis: line.units_basis || null,
      typedPrice: line.units_typed_price,
      typedCost: canViewCosts && line.units_typed_cost !== '' && line.units_typed_cost != null ? line.units_typed_cost : undefined
    });
    const price = plan.problem || plan.price.new === null ? orig.unit_price : plan.price.new;
    const cost = plan.problem ? orig.cost_price : (plan.cost.new === null ? '' : plan.cost.new);
    const next = { ...line, _unitsMoney: true, unit_price: price, cost_price: cost };
    if (next.unit_price !== line.unit_price || String(next.cost_price ?? '') !== String(line.cost_price ?? '') || !line._unitsMoney) changed = true;
    return next;
  });
  return changed ? out : lines;
}

/**
 * The note and the question on each corrected line (OrderLinesGrid
 * `lineFlags`): "2 stays 2 — now 2 × 500kg tote · 1.00 T (was 2.00 T)" and
 * "£369.00 on this line was per: [a tonne → £184.50 a 500kg tote, line
 * £369.00] [a 500kg tote → stays £369.00, line £738.00]". `render` builds the
 * control (the host's — buttons and, for typed, the boxes).
 * @returns {Object<string, object[]>} line id → flags
 */
export function unitsCorrectionFlags(lines, originals, itemOf = () => null) {
  const out = {};
  for (const line of lines || []) {
    const orig = line && originals?.get(line.id);
    if (!orig || !unitsChange(orig, line)) continue;
    const item = itemOf(line);
    const itemKg = item?.unit_weight_kg ?? orig.item_unit_weight_kg;
    const note = unitCorrectionNote({ quantity: line.quantity, before: orig, after: line,
      item: { itemUnitWeightKg: itemKg, accountCode: item?.sales_account_code } }) || 'Units corrected.';
    const choices = unitsMoneyChoices({ before: orig, after: line, itemUnitWeightKg: itemKg, price: orig.unit_price, quantity: line.quantity });
    const plan = unitsMoneyPlan({ before: orig, after: line, itemUnitWeightKg: itemKg, price: orig.unit_price, cost: orig.cost_price,
      basis: line.units_basis || null, typedPrice: line.units_typed_price,
      typedCost: line.units_typed_cost !== '' && line.units_typed_cost != null ? line.units_typed_cost : undefined });
    const basis = plan.basis || (choices[0] && choices[0].basis) || null;
    out[line.id] = [{
      key: 'units-corrected', tone: plan.problem ? 'error' : 'info', icon: plan.problem ? 'error' : 'straighten',
      text: `${note}${choices.length ? ` What was this line's price (${formatMoney(orig.unit_price)}) per?` : ''}`,
      unitsChoice: { lineId: line.id, basis, choices, problem: plan.problem, money: unitsMoneyText(plan) }
    }];
  }
  return out;
}

const formatMoney = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}` : '£0.00';
};

/** Did any line's units change after delivery (its answer is sent with the save)? */
export const unitsBasisFor = (line) => (line && line._unitsMoney ? (line.units_basis || 'old') : undefined);

/** The banner on the order screen after delivery. */
export function orderBannerText(state) {
  if (!state?.any) return null;
  return state.invoiced
    ? 'Invoiced. Words, item codes, units, the supplier, delivery terms and haulier can still be corrected — quantities, prices and tax stay as invoiced, and lines cannot be removed (a new line can be added; raise a debit for it). '
      + 'A units correction offers its invoice the same change before saving (a paid invoice is flagged instead, not changed). '
      + 'Its purchase orders are corrected in place for our records (nothing is sent to the supplier), and the invoice is offered the matching changes after you save.'
    : 'Delivered. Words, item codes, units (weight and pallets), the supplier, delivery terms and haulier can still be corrected — quantities stay as delivered and lines cannot be removed. '
      + 'A units correction asks what the line\'s price was per, and reaches its PO-D and PO-H lines too. '
      + 'Its purchase orders are corrected in place for our records (nothing is sent to the supplier).';
}

/**
 * The PO editor's closed cells after delivery (OrderLinesGrid `closed`),
 * from the PO's `afterDelivery` (routes/purchaseOrders.js changeRulesFor).
 */
export function poClosedCells(afterDelivery) {
  if (!afterDelivery) return null;
  const orders = (afterDelivery.orders || []).filter(Boolean);
  return {
    fields: new Set(['qty', 'price', 'tax', 'additives', 'add', 'remove']),
    reason: `${afterDelivery.reason || 'Its order is delivered.'} ${afterDelivery.closed || ''}`.trim(),
    // A line bought for an order line: its units are corrected on the order
    // (1.3.0 — it asks what the price was per and carries them here).
    unitsBlock: (line) => (line && line.orderLineId
      ? `Correct this line's units on its customer order${orders.length ? ` (${orders.join(', ')})` : ''} — it asks what the price was per and carries the units to this PO and its PO-H`
      : null)
  };
}

/** Does an invoice offer from a save have anything to show? */
export const hasInvoiceOffer = (offer) => !!offer && ((offer.proposals || []).length > 0 || (offer.notices || []).length > 0);

/** One proposed invoice line, in words: what changes, old → new. */
export function offerLineText(line) {
  const parts = [];
  const o = line?.old || {};
  const n = line?.new || {};
  const same = (a, b) => String(a ?? '').trim().toUpperCase() === String(b ?? '').trim().toUpperCase();
  if (String(o.description ?? '').trim() !== String(n.description ?? '').trim()) parts.push(`"${o.description || ''}" → "${n.description || ''}"`);
  if (!same(o.item_code, n.item_code)) parts.push(`item ${o.item_code || 'none'} → ${n.item_code || 'none'}`);
  if (!same(o.account_code, n.account_code)) parts.push(`account ${o.account_code || 'none'} → ${n.account_code || 'none'}`);
  return parts.join(', ');
}

/** The notices of an offer, in sentences. */
export function offerNotices(offer) {
  const out = [];
  for (const n of offer?.notices || []) {
    if (n.blocked) out.push(n.blocked.endsWith('.') ? n.blocked : `${n.blocked}.`);
    for (const u of n.unmatched || []) out.push(`${n.invoice_no || 'The invoice'}: ${u.product} could not be matched to a line of it — check it by hand.`);
    for (const l of n.left || []) out.push(`${n.invoice_no || 'The invoice'}: ${l.product} — ${l.note}.`);
    for (const a of n.added || []) out.push(`${a.product} is on the order but not on ${n.invoice_no || 'the invoice'} — raise a debit for it, or add it in the invoice editor.`);
  }
  return out;
}

/** Does Edit open the PO-H edit window (its delivery, collection and notes)? */
export function poEditOpensHaulageWindow(po) {
  return (po?.poType || po?.po_type) === 'haulage';
}

/**
 * How Edit opens a PO: the narrowed Cost & Supplied Qty window for a PO made
 * in Xero; the PO-H window for a PO-H (poEditOpensHaulageWindow); the full
 * editor otherwise, a delivered order's PO-D included (Cam, 2026-10-09).
 * @param {{ costEdit?: object|null, poType?: string, po_type?: string }} po
 */
export function poEditOpensNarrowed(po) {
  const scope = po?.costEdit;
  if (!scope || poEditOpensHaulageWindow(po)) return false;
  return scope.mode === 'xero_made';
}

/**
 * The separate "Cost & Supplied Qty" action beside Edit: offered on a
 * delivered order's PO-D (whose Edit is now the full editor). Elsewhere it is
 * there and disabled, with why — never hidden.
 * @returns {{ enabled: boolean, title: string }}
 */
export function poCostSuppliedAction(po) {
  const scope = po?.costEdit;
  if (scope && poEditOpensHaulageWindow(po)) {
    return { enabled: true, title: `Supplied quantities — ${scope.reason}` };
  }
  if (poEditOpensHaulageWindow(po)) {
    return { enabled: false, title: 'A PO-H\'s supplied quantities are recorded once its order is delivered' };
  }
  if (scope && scope.mode === 'delivered' && !poEditOpensNarrowed(po)) {
    return { enabled: true, title: `Cost & Supplied Qty — ${scope.reason}` };
  }
  if (scope && poEditOpensNarrowed(po)) {
    return { enabled: false, title: 'Use Edit — it opens the cost and supplied quantity window for this PO' };
  }
  return { enabled: false, title: 'Cost & Supplied Qty is for a delivered order\'s PO — before delivery, Edit changes the cost and quantity' };
}
